<template>
  <div class="toaster" role="region" aria-label="Notifications">
    <transition-group name="toast" tag="div" class="toaster__stack">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        :class="['toast', `toast--${toast.type}`]"
        role="status"
        aria-live="polite"
        @mouseenter="pause(toast.id)"
        @mouseleave="resume(toast.id)"
      >
        <span class="toast__icon" aria-hidden="true" v-html="iconFor(toast.type)"></span>
        <div class="toast__body">
          <div class="toast__message">{{ toast.message }}</div>
          <div v-if="toast.description" class="toast__description">{{ toast.description }}</div>
        </div>
        <button
          v-if="toast.action"
          type="button"
          class="toast__action"
          @click="runAction(toast)"
        >{{ toast.action.label }}</button>
        <button
          type="button"
          class="toast__close"
          aria-label="Dismiss notification"
          @click="dismiss(toast.id)"
        >
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script>
import toast from '@/utils/toast'

const ICONS = {
  success: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
  error:   '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>',
  warning: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>',
  info:    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
}

export default {
  name: 'AppToast',
  data () {
    return {
      timers: new Map(),
      remaining: new Map()
    }
  },
  computed: {
    toasts () {
      return toast.state.toasts
    }
  },
  watch: {
    toasts: {
      immediate: true,
      handler (current) {
        const ids = new Set(current.map((t) => t.id))
        // Drop timers for toasts that no longer exist.
        for (const id of Array.from(this.timers.keys())) {
          if (!ids.has(id)) {
            clearTimeout(this.timers.get(id))
            this.timers.delete(id)
            this.remaining.delete(id)
          }
        }
        // Schedule timers for new ones.
        for (const t of current) {
          if (this.timers.has(t.id)) continue
          if (t.persistent || t.duration <= 0) continue
          this.scheduleDismiss(t.id, t.duration)
        }
      }
    }
  },
  beforeDestroy () {
    for (const handle of this.timers.values()) clearTimeout(handle)
    this.timers.clear()
    this.remaining.clear()
  },
  methods: {
    scheduleDismiss (id, duration) {
      const startedAt = Date.now()
      this.remaining.set(id, { duration, startedAt })
      const handle = setTimeout(() => {
        toast.dismiss(id)
      }, duration)
      this.timers.set(id, handle)
    },
    pause (id) {
      if (!this.timers.has(id)) return
      clearTimeout(this.timers.get(id))
      this.timers.delete(id)
      const meta = this.remaining.get(id)
      if (meta) {
        const elapsed = Date.now() - meta.startedAt
        meta.duration = Math.max(0, meta.duration - elapsed)
        this.remaining.set(id, meta)
      }
    },
    resume (id) {
      const meta = this.remaining.get(id)
      if (!meta) return
      if (meta.duration <= 0) {
        toast.dismiss(id)
        return
      }
      this.scheduleDismiss(id, meta.duration)
    },
    dismiss (id) {
      toast.dismiss(id)
    },
    runAction (t) {
      try { t.action.onClick() } catch (error) { console.log(error) }
      toast.dismiss(t.id)
    },
    iconFor (type) {
      return ICONS[type] || ICONS.info
    }
  }
}
</script>

<style scoped>
.toaster {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 9999;
  pointer-events: none;
  max-width: min(420px, calc(100vw - 2rem));
}

.toaster__stack {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  align-items: flex-end;
}

.toast {
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  width: 100%;
  min-width: 280px;
  padding: 0.7rem 0.85rem;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06);
  font-size: 0.85rem;
  color: #1f2a44;
  border-left: 4px solid #94a3b8;
}

.toast--success { border-left-color: #16a07a; }
.toast--error   { border-left-color: #dc2626; }
.toast--warning { border-left-color: #f59e0b; }
.toast--info    { border-left-color: #1e40af; }

.toast__icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  margin-top: 1px;
}

.toast--success .toast__icon { background: #ecfdf5; color: #0e5b46; }
.toast--error   .toast__icon { background: #fef2f2; color: #991b1b; }
.toast--warning .toast__icon { background: #fffbeb; color: #92400e; }
.toast--info    .toast__icon { background: #eff6ff; color: #1e40af; }

.toast__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding-top: 2px;
}

.toast__message {
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
  word-break: break-word;
}

.toast__description {
  font-size: 0.78rem;
  color: #4b5563;
  line-height: 1.4;
  word-break: break-word;
}

.toast__action {
  flex-shrink: 0;
  align-self: center;
  height: 28px;
  padding: 0 0.65rem;
  border: 1px solid #d8dde6;
  border-radius: 6px;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.toast__action:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.toast__close {
  flex-shrink: 0;
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-top: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.toast__close:hover {
  background: #f1f5f9;
  color: #1f2a44;
}

/* ── Enter / leave animation ── */
.toast-enter-active,
.toast-leave-active {
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.22s ease;
}
.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.96);
}
.toast-leave-active {
  position: absolute;
  right: 0;
}

@media (max-width: 575px) {
  .toaster {
    bottom: 0.75rem;
    right: 0.75rem;
    left: 0.75rem;
    max-width: none;
  }
  .toaster__stack {
    align-items: stretch;
  }
  .toast {
    min-width: 0;
  }
}
</style>
